<template>
  <v-row>
    <v-col cols="12">
      <div class="d-flex align-center mb-5">
        <v-btn icon class="mr-2" @click="GoBack()" title="Wróć">
          <v-icon>fas fa-arrow-left</v-icon>
        </v-btn>
        <div class="text-h5">{{ $t("admin.matchesTitle") }}</div>
      </div>

      <div class="text-caption grey--text text--darken-1 mb-4">
        {{ $t("admin.matchesHint") }}
      </div>

      <v-card>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" lg="4">
              <v-select
                dense
                outlined
                hide-details
                :label="$t('admin.selectCompetition')"
                :items="competitions"
                item-text="competitionsFullName"
                item-value="competitionsId"
                v-model="competitionsId"
                :loading="competitionsLoading"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="6" lg="4">
              <v-select
                dense
                outlined
                hide-details
                :label="$t('common.gameweek')"
                :items="gameweeks"
                v-model="gameweekName"
                :disabled="!gameweeks.length"
              ></v-select>
            </v-col>
          </v-row>

          <v-data-table
            :headers="headers"
            :items="gameweekItems"
            :items-per-page="-1"
            :loading="loading"
            hide-default-footer
            group-by="matchDate"
            sort-by="matchDateTime"
            disable-sort
            class="elevation-1 mt-3"
            :no-data-text="$t('admin.noMatches')"
          >
            <template v-slot:[`group.header`]="{ items }">
              <th :colspan="headers.length">
                {{ items[0].matchDateFriendly }}
              </th>
            </template>

            <template v-slot:[`item.matchTime`]="{ item }">
              {{ item.matchTime }}
            </template>

            <template v-slot:[`item.teams`]="{ item }">
              {{ item.homeTeam.teamName }} - {{ item.awayTeam.teamName }}
            </template>

            <!-- WYNIK -->
            <template v-slot:[`item.result`]="{ item }">
              <div
                v-if="IsEditing(item)"
                class="d-flex"
                style="align-items: baseline; justify-content: center"
              >
                <div style="width: 40px">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    v-mask="'##'"
                    v-model.number="editState[item.matchId].home"
                    :disabled="!EditIsFinished(item)"
                  ></v-text-field>
                </div>
                <div class="mx-1"><span>:</span></div>
                <div style="width: 40px">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    v-mask="'##'"
                    v-model.number="editState[item.matchId].away"
                    :disabled="!EditIsFinished(item)"
                  ></v-text-field>
                </div>
              </div>
              <span v-else>
                <span v-if="HasScore(item)">
                  {{ item.homeTeamScore }} : {{ item.awayTeamScore }}
                </span>
              </span>
            </template>

            <!-- STATUS -->
            <template v-slot:[`item.status`]="{ item }">
              <div v-if="IsEditing(item)" style="min-width: 150px">
                <v-select
                  dense
                  outlined
                  hide-details
                  :items="statusOptions"
                  v-model="editState[item.matchId].statusId"
                ></v-select>
              </div>
              <span v-else>{{ StatusLabel(item.statusCode) }}</span>
            </template>

            <!-- AKCJE -->
            <template v-slot:[`item.actions`]="{ item }">
              <div v-if="IsEditing(item)" class="d-flex justify-end">
                <v-btn
                  icon
                  small
                  color="primary"
                  :loading="editState[item.matchId].loading"
                  :title="$t('admin.saveMatch')"
                  @click="SaveMatch(item)"
                >
                  <v-icon small>fas fa-save</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  :title="$t('admin.cancel')"
                  @click="CancelEdit(item)"
                >
                  <v-icon small>fas fa-times</v-icon>
                </v-btn>
              </div>
              <v-btn
                v-else
                icon
                small
                color="primary"
                :title="$t('admin.editMatch')"
                @click="StartEdit(item)"
              >
                <v-icon small>fas fa-pencil-alt</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import AdminService from "@/services/AdminService.js";
import dayjs from "@/plugins/dayjs.js";
import { mapGetters, mapActions } from "vuex";

// StatusId zgodny z MatchStatusEnum (backend). FINISHED = 3.
const FINISHED_STATUS_ID = 3;
const MATCH_STATUS_CODES = [
  { id: 1, code: "NOT_STARTED" },
  { id: 2, code: "IN_PROGRESS" },
  { id: 3, code: "FINISHED" },
  { id: 4, code: "SUSPENSED" },
  { id: 5, code: "INTERRUPTED" },
  { id: 6, code: "POSTPONED" },
  { id: 7, code: "CANCELLED" },
  { id: 8, code: "ABANDONED" },
];

export default {
  name: "AdminMatchesView",
  data() {
    return {
      competitions: [],
      competitionsId: null,
      competitionsLoading: true,
      matches: [],
      gameweekName: "",
      loading: false,
      editState: {},
      headers: [
        { value: "matchTime", text: "", width: "67px" },
        { value: "teams", text: "Mecz" },
        { value: "result", text: this.$t("admin.result"), align: "center" },
        { value: "status", text: this.$t("admin.status"), align: "center" },
        {
          value: "actions",
          text: this.$t("admin.actions"),
          align: "end",
          width: "90px",
        },
      ],
    };
  },
  computed: {
    statusOptions() {
      return MATCH_STATUS_CODES.map((s) => ({
        value: s.id,
        text: this.$t(`admin.matchStatuses.${s.code}`),
      }));
    },
    gameweeks() {
      return [...new Set(this.matches.map((x) => x.gameweekName))].sort(
        (a, b) => a - b
      );
    },
    gameweekItems() {
      return this.matches.filter((x) => x.gameweekName == this.gameweekName);
    },
  },
  watch: {
    async competitionsId() {
      await this.$_loadMatches();
    },
  },
  async mounted() {
    await this.setCompetitions();
    this.competitions = this.getCompetitions();
    this.competitionsLoading = false;
    if (this.competitions[0]) {
      this.competitionsId = this.competitions[0].competitionsId;
    }
  },
  methods: {
    ...mapGetters("common", ["getCompetitions"]),
    ...mapActions("common", ["setCompetitions"]),
    GoBack() {
      this.$router.push({ name: "AdminPanel" }).catch(() => {});
    },
    StatusLabel(statusCode) {
      const known = MATCH_STATUS_CODES.find((x) => x.code === statusCode);
      return known ? this.$t(`admin.matchStatuses.${statusCode}`) : statusCode;
    },
    HasScore(item) {
      return item.homeTeamScore != null && item.awayTeamScore != null;
    },
    IsEditing(item) {
      const s = this.editState[item.matchId];
      return !!(s && s.editing);
    },
    EditIsFinished(item) {
      const s = this.editState[item.matchId];
      return !!(s && s.statusId === FINISHED_STATUS_ID);
    },
    StartEdit(item) {
      this.$set(this.editState, item.matchId, {
        editing: true,
        loading: false,
        home: item.homeTeamScore,
        away: item.awayTeamScore,
        statusId: item.statusId,
      });
    },
    CancelEdit(item) {
      this.$set(this.editState, item.matchId, {
        editing: false,
        loading: false,
        home: null,
        away: null,
        statusId: null,
      });
    },
    async SaveMatch(item) {
      const matchId = item.matchId;
      const state = this.editState[matchId];
      const matchLabel = `${item.homeTeam.teamName} : ${item.awayTeam.teamName}`;
      const isFinished = state.statusId === FINISHED_STATUS_ID;

      const home = state.home === "" ? null : state.home;
      const away = state.away === "" ? null : state.away;

      if (isFinished && (home == null || away == null)) {
        this.$vToastify.validationError("Mecz zakończony wymaga obu wyników");
        return;
      }

      try {
        this.$set(this.editState[matchId], "loading", true);

        const response = await AdminService.UpdateMatch(matchId, {
          homeTeamScore: isFinished ? home : null,
          awayTeamScore: isFinished ? away : null,
          statusId: state.statusId,
        });

        if (response.status === 200) {
          const data = response.data || {};
          const dbMatch = data.data;

          // Safeguard: odzwierciedl autorytatywny stan meczu z bazy.
          if (dbMatch) {
            item.homeTeamScore = dbMatch.homeTeamScore;
            item.awayTeamScore = dbMatch.awayTeamScore;
            item.statusId = dbMatch.statusId;
            item.statusCode = dbMatch.statusCode;
          }

          if ((data.errors || [])[0]) {
            this.$vToastify.customWarning(data.errors[0]);
          } else {
            this.$vToastify.customSuccess(`Zapisano mecz: ${matchLabel}`);
            this.CancelEdit(item);
          }
        } else {
          this.$vToastify.customError(
            `Nie udało się zapisać meczu: ${matchLabel}`
          );
        }
      } catch (err) {
        this.$vToastify.customError(
          `Nie udało się zapisać meczu: ${matchLabel}`
        );
      } finally {
        if (this.editState[matchId]) {
          this.$set(this.editState[matchId], "loading", false);
        }
      }
    },
    async $_loadMatches() {
      if (!this.competitionsId) {
        this.matches = [];
        return;
      }
      try {
        this.loading = true;
        this.editState = {};
        const response = await AdminService.GetMatches(this.competitionsId);
        const result = response.data;
        if (result.success) {
          this.matches = result.data.map((x) => ({
            ...x,
            matchDate: this.$_getMatchDate(x.matchDateTime),
            matchDateFriendly: this.$_getMatchDateFriendly(x.matchDateTime),
            matchTime: this.$_getMatchTime(x.matchDateTime),
          }));
          this.$_setCurrentGameweek();
        }
      } catch (err) {
        //
      } finally {
        this.loading = false;
      }
    },
    $_setCurrentGameweek() {
      // Admin koryguje głównie mecze już rozegrane - domyślnie pokaż najnowszą kolejkę z wynikiem,
      // a jeśli nie ma - ostatnią dostępną.
      const played = this.matches.filter((x) => this.HasScore(x));
      const source = played.length ? played : this.matches;
      const ordered = [...source].sort(
        (a, b) => dayjs(a.matchDateTime) - dayjs(b.matchDateTime)
      );
      this.gameweekName =
        (ordered[ordered.length - 1] || {}).gameweekName || "";
    },
    $_getMatchDate(dateTime) {
      return dayjs(dateTime).format("YYYY MMMM DD");
    },
    $_getMatchDateFriendly(dateTime) {
      return dayjs(dateTime).format("DD MMMM YYYY, dddd");
    },
    $_getMatchTime(dateTime) {
      return dayjs(dateTime).format("HH:mm");
    },
  },
};
</script>
